import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator, Firestore } from 'firebase/firestore';
import { getAuth, connectAuthEmulator, Auth } from 'firebase/auth';
import { getStorage, connectStorageEmulator, FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, Functions } from 'firebase/functions';
import { getDatabase, connectDatabaseEmulator, Database } from 'firebase/database';
import { getPerformance, FirebasePerformance } from 'firebase/performance';
import {
  AppStore,
  createStore,
  DEFAULT_FIREBASE_REGION,
  FirestoreUtils,
  StoreDependencies,
} from '@akademiasaas/shared';
import { ThunkDispatch, AnyAction } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { NoOpAnalyticsService, WebAnalyticsService } from '~/services/WebAnalyticsService';

const {
  VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID,
  VITE_FIREBASE_MEASUREMENT_ID,
  VITE_FIREBASE_EMULATOR,
  VITE_USE_ANALYTICS,
  VITE_USE_PERFORMANCE_MONITORING,
} = import.meta.env;

export const firebaseConfig = {
  apiKey: VITE_FIREBASE_API_KEY,
  authDomain: VITE_FIREBASE_AUTH_DOMAIN,
  projectId: VITE_FIREBASE_PROJECT_ID,
  storageBucket: VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: VITE_FIREBASE_APP_ID,
  measurementId: VITE_FIREBASE_MEASUREMENT_ID,
};

export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const db: Firestore = getFirestore(app);
export const auth: Auth = getAuth(app);
export const functions: Functions = getFunctions(app, DEFAULT_FIREBASE_REGION);
export const database: Database = getDatabase(app);

let _storage: FirebaseStorage | null = null;
export const getStorageInstance = (): FirebaseStorage => {
  if (!_storage) {
    _storage = getStorage(app, VITE_FIREBASE_STORAGE_BUCKET);
  }
  return _storage;
};
export const storage = getStorageInstance();

if (VITE_FIREBASE_EMULATOR === 'true') {
  // eslint-disable-next-line no-console
  console.warn('Running on Firebase emulator!');

  // Use the current hostname (works for both localhost and IP access)
  const emulatorHost = window.location.hostname;

  connectFirestoreEmulator(db, emulatorHost, 8080);
  connectAuthEmulator(auth, `http://${emulatorHost}:9099`);
  connectStorageEmulator(storage, emulatorHost, 9199);
  connectFunctionsEmulator(functions, emulatorHost, 5001);
  connectDatabaseEmulator(database, emulatorHost, 9000);
}

export const perf: FirebasePerformance | null = VITE_USE_PERFORMANCE_MONITORING === 'true' ? getPerformance(app) : null;
export const analyticsService =
  VITE_USE_ANALYTICS === 'true'
    ? new WebAnalyticsService()
    : new NoOpAnalyticsService();

export const { store } = createStore({
  firestore: () => db,
  auth: () => auth,
  analytics: analyticsService,
  db: db,
  functions: functions,
  database: () => database,
  config: {
    APP_FUNCTION_DOMAIN: import.meta.env.VITE_FUNCTION_DOMAIN,
  },
} as any);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = ThunkDispatch<AppStore, StoreDependencies & FirestoreUtils, AnyAction>;
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
